import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Center, Spinner, useDisclosure } from '@chakra-ui/react';
import { supabase } from '../lib/supabaseClient';
import { SubscriptionModal, SpotifyRequestModal, SpotifyPendingModal } from './PlayerAccessModals';

interface PlayerAccessRouteProps {
  children: React.ReactNode;
}

interface PlayerAccess {
  id: string;
  user_id: string;
  spotify_access: boolean;
  apple_music_access: boolean;
  spotify_email: string | null;
  spotify_name: string | null;
  created_at: string;
  updated_at: string;
}

export function PlayerAccessRoute({ children }: PlayerAccessRouteProps) {
  const { user, isSubscriber, loading } = useAuth();
  const location = useLocation();
  const [playerAccessData, setPlayerAccessData] = useState<PlayerAccess | null>(null);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  
  // Modal controls
  const {
    isOpen: isSubscriptionModalOpen,
    onOpen: onOpenSubscriptionModal,
    onClose: onCloseSubscriptionModal,
  } = useDisclosure();
  
  const {
    isOpen: isSpotifyRequestModalOpen,
    onOpen: onOpenSpotifyRequestModal,
    onClose: onCloseSpotifyRequestModal,
  } = useDisclosure();
  
  const {
    isOpen: isSpotifyPendingModalOpen,
    onOpen: onOpenSpotifyPendingModal,
    onClose: onCloseSpotifyPendingModal,
  } = useDisclosure();

  useEffect(() => {
    // Only check player access if user is logged in and we're done loading auth state
    if (user && !loading) {
      checkPlayerAccess(user.id);
    } else if (!loading && !user) {
      setIsCheckingAccess(false);
    }
  }, [user, loading]);

  const checkPlayerAccess = async (userId: string) => {
    try {
      // Check if player access exists for this user
      const { data, error } = await supabase
        .from('player_access')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking player access:', error);
      }

      setPlayerAccessData(data);
      setIsCheckingAccess(false);

      // Determine which modal to show if any
      if (!data && !isSubscriber) {
        // Not a subscriber and no entry in the table
        onOpenSubscriptionModal();
      } else if (data && !data.spotify_access && !data.spotify_email) {
        // Has an entry but no Spotify info provided yet
        onOpenSpotifyRequestModal();
      } else if (data && !data.spotify_access && data.spotify_email) {
        // Has provided Spotify info but not approved yet
        onOpenSpotifyPendingModal();
      }
    } catch (error) {
      console.error('Error in checkPlayerAccess:', error);
      setIsCheckingAccess(false);
    }
  };

  // Show loading spinner while checking authentication and access status
  if (loading || isCheckingAccess) {
    console.log('PlayerAccessRoute - Auth/Access is loading');
    return (
      <Center h="100vh">
        <Spinner 
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="blue.500"
          size="xl"
        />
      </Center>
    );
  }
  
  // Redirect to signup if not authenticated
  if (!user) {
    console.log('PlayerAccessRoute - No user, redirecting to signup');
    return <Navigate to="/signup" state={{ from: location }} replace />;
  }

  // Check if user has appropriate access for the player
  if (playerAccessData && playerAccessData.spotify_access) {
    // User has access, render the component
    console.log('PlayerAccessRoute - User has player access, rendering protected content');
    return (
      <>
        {children}
      </>
    );
  }
  
  // If user doesn't have access, still render but with modals
  return (
    <>
      {children}
      
      {/* Modals for different access states */}
      <SubscriptionModal 
        isOpen={isSubscriptionModalOpen} 
        onClose={onCloseSubscriptionModal} 
      />
      
      <SpotifyRequestModal 
        isOpen={isSpotifyRequestModalOpen} 
        onClose={onCloseSpotifyRequestModal} 
        userId={user.id}
      />
      
      <SpotifyPendingModal 
        isOpen={isSpotifyPendingModalOpen} 
        onClose={onCloseSpotifyPendingModal} 
      />
    </>
  );
} 